import React, { useState, useEffect } from 'react';
import { Box, Fab, Zoom, Badge, useMediaQuery, useTheme } from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CallIcon from '@mui/icons-material/Call';
import TranslateIcon from '@mui/icons-material/Translate';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from './LanguageProvider';

export const FloatingButtons = () => {
  const { currentLang, changeLanguage } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showPulse, setShowPulse] = useState(true);
  const [showNotification, setShowNotification] = useState(true);
  const [hoveredButton, setHoveredButton] = useState(null);

  const direction = currentLang === 'ar' ? 'rtl' : 'ltr';

  // الأرقام الصحيحة من البيزنس كارد
  const phoneNumber = '+971503330841';
  const whatsappNumber = '+971503330841';

  // Effect for pulse animation
  useEffect(() => {
    const interval = setInterval(() => {
      setShowPulse(prev => !prev);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Effect for notification (disappears after 10 seconds)
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowNotification(false);
    }, 10000);
    return () => clearTimeout(timer);
  }, []);

  const handleWhatsAppClick = () => {
    // نص الترحيب حسب اللغة
    const welcomeMessage = currentLang === 'ar' 
      ? 'مرحباً، أحتاج إلى استشارة قانونية'
      : 'Hello, I need legal consultation';
    window.open(`https://wa.me/${whatsappNumber}?text=${encodeURIComponent(welcomeMessage)}`, '_blank');
  };

  const handleCallClick = () => {
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleLanguageToggle = () => {
    changeLanguage(currentLang === 'en' ? 'ar' : 'en');
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
        delay: 2,
        staggerChildren: 0.15
      }
    }
  };

  const buttonVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: (i) => ({
      scale: 1,
      rotate: 0,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 15,
        delay: 2 + (i * 0.15)
      }
    }),
    hover: {
      scale: 1.15,
      rotate: 5,
      boxShadow: "0 15px 35px rgba(0,0,0,0.4)",
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 10
      }
    },
    tap: {
      scale: 0.9,
      rotate: -5,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 10
      }
    }
  };

  const pulseVariants = {
    initial: { scale: 1, opacity: 0.8 },
    pulse: {
      scale: [1, 1.3, 1.6, 1.3, 1],
      opacity: [0.8, 0.5, 0.2, 0.5, 0.8],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  const notificationVariants = {
    initial: { scale: 0, opacity: 0, y: 20 },
    animate: {
      scale: 1,
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        type: "spring",
        stiffness: 300
      }
    },
    exit: {
      scale: 0,
      opacity: 0,
      y: 20,
      transition: { duration: 0.3 }
    }
  };

  const labelVariants = {
    initial: { opacity: 0, scale: 0.8, x: direction === 'rtl' ? 10 : -10 },
    animate: { 
      opacity: 1, 
      scale: 1, 
      x: 0,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 15
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.8, 
      x: direction === 'rtl' ? 10 : -10,
      transition: { duration: 0.2 }
    }
  };

  const textContent = {
    en: {
      whatsapp: "Chat on WhatsApp",
      call: "Call us now",
      language: "Change Language",
      languageBtn: "العربية",
      notification: "Contact us now!"
    },
    ar: {
      whatsapp: "تواصل عبر واتساب",
      call: "اتصل بنا الآن",
      language: "تغيير اللغة",
      languageBtn: "English",
      notification: "تواصل معنا الآن!"
    }
  };

  // تحديد حجم الأيقونات حسب الشاشة
  const getFabSize = () => {
    if (isMobile) {
      return { width: 48, height: 48 };
    }
    return { width: 56, height: 56 };
  };

  const getIconSize = () => {
    if (isMobile) {
      return 24;
    }
    return 28;
  };

  const fabSize = getFabSize();
  const iconSize = getIconSize();

  // الأزرار - مع إضافة زر اللغة في الموبايل
  const buttons = [
    {
      id: 'whatsapp',
      icon: WhatsAppIcon,
      color: '#25D366',
      gradient: 'linear-gradient(45deg, #25D366 30%, #128C7E 90%)',
      hoverGradient: 'linear-gradient(45deg, #128C7E 30%, #25D366 90%)',
      onClick: handleWhatsAppClick,
      label: textContent[currentLang].whatsapp,
      tooltip: textContent[currentLang].whatsapp,
      show: true
    },
    {
      id: 'call',
      icon: CallIcon,
      color: '#2196F3',
      gradient: 'linear-gradient(45deg, #2196F3 30%, #1976D2 90%)',
      hoverGradient: 'linear-gradient(45deg, #1976D2 30%, #2196F3 90%)',
      onClick: handleCallClick,
      label: textContent[currentLang].call,
      tooltip: textContent[currentLang].call,
      show: true
    }
  ];

  // إضافة زر اللغة في الموبايل فقط
  if (isMobile) {
    buttons.push({
      id: 'language',
      icon: TranslateIcon,
      color: '#d4af37',
      gradient: 'linear-gradient(45deg, #d4af37 30%, #b8942f 90%)',
      hoverGradient: 'linear-gradient(45deg, #b8942f 30%, #d4af37 90%)',
      onClick: handleLanguageToggle,
      label: textContent[currentLang].language,
      tooltip: textContent[currentLang].language,
      show: true
    });
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      style={{
        position: 'fixed',
        bottom: 30,
        [direction === 'rtl' ? 'left' : 'right']: 30,
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? 12 : 15,
        zIndex: 10008,
        pointerEvents: 'none'
      }}
    >
      {/* Notification Bubble */}
      {!isMobile && (
        <AnimatePresence>
          {showNotification && (
            <motion.div
              variants={notificationVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              style={{
                position: 'absolute',
                [direction === 'rtl' ? 'right' : 'left']: '100%',
                top: 20,
                marginRight: direction === 'rtl' ? 15 : 0,
                marginLeft: direction === 'rtl' ? 0 : 15,
                background: 'linear-gradient(135deg, #d4af37 0%, #b8942f 100%)',
                color: '#0b0c1e',
                padding: '10px 20px',
                borderRadius: '30px',
                boxShadow: '0 10px 25px rgba(212,175,55,0.3)',
                whiteSpace: 'nowrap',
                fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                fontSize: '0.95rem',
                fontWeight: 600,
                zIndex: 2001,
                pointerEvents: 'none'
              }}
            >
              <motion.span
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 0.5, delay: 0.5 }}
                style={{ display: 'inline-block', marginRight: 8 }}
              >
                👋
              </motion.span>
              {textContent[currentLang].notification}
            </motion.div>
          )}
        </AnimatePresence>
      )}

      {/* الأزرار */}
      {buttons.map((button, index) => {
        const Icon = button.icon;
        const isHovered = hoveredButton === button.id;

        return (
          <Box key={button.id} sx={{ position: 'relative', pointerEvents: 'auto' }}>
            {/* Pulse Effect */}
            <motion.div
              variants={pulseVariants}
              initial="initial"
              animate={showPulse && isHovered ? "pulse" : "initial"}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: '50%',
                background: `radial-gradient(circle, ${button.color}80 0%, ${button.color}00 70%)`,
                zIndex: -1
              }}
            />

            {/* Badge للتنبيه */}
            <Badge
              color="error"
              variant="dot"
              invisible={!showNotification || isMobile || button.id === 'language'}
              sx={{
                '& .MuiBadge-badge': {
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: button.color,
                  [direction === 'rtl' ? 'right' : 'left']: '15%',
                  top: '15%',
                  boxShadow: '0 0 0 2px white',
                  animation: showPulse ? 'pulse 2s infinite' : 'none'
                }
              }}
            >
              <motion.div
                custom={index}
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
                onHoverStart={() => setHoveredButton(button.id)}
                onHoverEnd={() => setHoveredButton(null)}
              >
                <Fab
                  onClick={button.onClick}
                  sx={{
                    width: fabSize.width,
                    height: fabSize.height,
                    background: button.gradient,
                    color: 'white',
                    boxShadow: `0 8px 20px ${button.color}60`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: button.hoverGradient,
                      boxShadow: `0 12px 25px ${button.color}80`,
                    }
                  }}
                >
                  <motion.div
                    animate={{ 
                      rotate: isHovered ? [0, 10, -10, 0] : 0,
                      scale: isHovered ? [1, 1.2, 1] : 1
                    }}
                    transition={{ duration: 0.5 }}
                  >
                    <Icon sx={{ fontSize: iconSize }} />
                  </motion.div>
                </Fab>
              </motion.div>
            </Badge>

            {/* Floating Label */}
            {!isMobile && (
              <AnimatePresence>
                {isHovered && (
                  <motion.div
                    variants={labelVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    style={{
                      position: 'absolute',
                      [direction === 'rtl' ? 'left' : 'right']: '100%',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      marginRight: direction === 'rtl' ? 10 : 0,
                      marginLeft: direction === 'rtl' ? 0 : 10,
                      background: button.id === 'language' ? '#d4af37' : button.color,
                      color: button.id === 'language' ? '#0b0c1e' : 'white',
                      padding: '6px 14px',
                      borderRadius: '30px',
                      fontSize: '0.85rem',
                      whiteSpace: 'nowrap',
                      fontFamily: currentLang === 'ar' ? 'Cairo' : 'Roboto',
                      fontWeight: 500,
                      boxShadow: '0 5px 15px rgba(0,0,0,0.15)',
                      pointerEvents: 'none',
                      zIndex: 2002
                    }}
                  >
                    {button.label}
                  </motion.div>
                )}
              </AnimatePresence>
            )}
          </Box>
        );
      })}

      <style>
        {`
          @keyframes pulse {
            0% {
              transform: scale(0.8);
              opacity: 0.5;
            }
            100% {
              transform: scale(1.2);
              opacity: 0;
            }
          }
        `}
      </style>
    </motion.div>
  );
};

export default FloatingButtons;